import type {
  CalculateTipsResponse,
  WorkGroup,
  WorkGroupPayload,
} from "model/Tip";
import type { TipDistributionDraft } from "../../logic/tips.v2.logic";
import type {
  EditableTipEntry,
  EditableTipsByDate,
  TeamPercentages,
  TeamRows,
} from "./tips.distributionModal.types";
export {
  getWorkerInitials,
  getWorkerNameWithIdFallback as getWorkerName,
} from "../../logic/tips.worker.logic";

interface ToEditableTipsParams {
  readonly calculationResult: CalculateTipsResponse | null;
}

interface ToWorkGroupPayloadParams {
  readonly teamPercentages: TeamPercentages;
  readonly teamRows: TeamRows;
}

interface UpdateEditableTipWorkerAmountParams {
  readonly currentTips: EditableTipsByDate;
  readonly date: string;
  readonly value: string;
  readonly workerIndex: number;
}

export function toEditableTips({
  calculationResult,
}: ToEditableTipsParams): EditableTipsByDate {
  if (!calculationResult) return {};

  return calculationResult.companyTips.reduce<EditableTipsByDate>(
    (tipsByDate, tip) => ({
      ...tipsByDate,
      [tip.date]: {
        amount: tip.amount,
        companyTipId: tip.companyTipId,
        remainingAmount: tip.remainingAmount,
        workers: tip.workers.map((worker) => ({ ...worker })),
      },
    }),
    {},
  );
}

export function toAmountInputValue(value: number): string {
  return Math.round(value).toString();
}

export function toTeamRows(
  calculationResult: CalculateTipsResponse,
): WorkGroup[] {
  const workGroupsById = new Map<number, WorkGroup>();
  calculationResult.workGroups.forEach((group) => {
    workGroupsById.set(group.workGroupId, group);
  });

  calculationResult.companyTips.forEach((tip) => {
    tip.workGroups.forEach((group) => {
      workGroupsById.set(group.workGroupId, group);
    });
  });

  return Array.from(workGroupsById.values());
}

export function toWorkGroupPayload({
  teamPercentages,
  teamRows,
}: ToWorkGroupPayloadParams): WorkGroupPayload[] {
  return teamRows.map((group) => ({
    id: group.workGroupId,
    percentage: Number(teamPercentages[group.workGroupId]) || 0,
  }));
}

export function toHourDistributionDrafts(
  editableTipEntries: readonly EditableTipEntry[],
): TipDistributionDraft[] {
  return editableTipEntries.map(([, tip]) => ({
    companyTipId: tip.companyTipId,
    remainingAmount: tip.remainingAmount,
    workers: tip.workers.map((worker) => ({
      workerId: worker.workerId,
      workTime: worker.workTime,
      tipAmount: worker.tipAmount,
      workGroupId: worker.workGroupId,
    })),
  }));
}

export function toResetDistributionDrafts(
  editableTipEntries: readonly EditableTipEntry[],
): TipDistributionDraft[] {
  return editableTipEntries.map(([, tip]) => ({
    companyTipId: tip.companyTipId,
    remainingAmount: tip.amount,
    workers: tip.workers.map((worker) => ({
      workerId: worker.workerId,
      workTime: worker.workTime,
      tipAmount: 0,
      workGroupId: worker.workGroupId,
    })),
  }));
}

export function toInitialTeamPercentages(teamRows: TeamRows): TeamPercentages {
  return teamRows.reduce<TeamPercentages>(
    (values, group) => ({
      ...values,
      [group.workGroupId]:
        group.percentage > 0 ? Math.round(group.percentage).toString() : "",
    }),
    {},
  );
}

export function updateEditableTipWorkerAmount({
  currentTips,
  date,
  value,
  workerIndex,
}: UpdateEditableTipWorkerAmountParams): EditableTipsByDate {
  const currentTip = currentTips[date];
  if (!currentTip) return currentTips;

  const nextTipAmount = value.trim().length === 0 ? 0 : Number(value);
  const totalDistributedByOthers = currentTip.workers.reduce(
    (sum, worker, index) =>
      index === workerIndex ? sum : sum + worker.tipAmount,
    0,
  );

  if (totalDistributedByOthers + nextTipAmount > currentTip.amount) {
    return currentTips;
  }

  const nextWorkers = currentTip.workers.map((worker, index) =>
    index === workerIndex
      ? {
          ...worker,
          tipAmount: nextTipAmount,
        }
      : worker,
  );

  return {
    ...currentTips,
    [date]: {
      ...currentTip,
      remainingAmount:
        currentTip.amount - (totalDistributedByOthers + nextTipAmount),
      workers: nextWorkers,
    },
  };
}
